import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { CameraErrorState } from "@/components/scanner/CameraErrorState";
import { Viewfinder, type ScanPhase } from "@/components/scanner/Viewfinder";
import { CAMERA_ERROR_STATES, useCamera, type CameraStatus } from "@/hooks/useCamera";
import { cn } from "@/lib/utils";

const PROCESS_MS = 2200;
const PHASE_STATUS: Record<ScanPhase, string> = { ready: "CAMERA_READY", scanning: "SCANNING", processing: "PROCESSING" };
const STATUS_LABEL: Partial<Record<CameraStatus, string>> = {
  idle: "CAMERA_PERMISSION_REQUIRED",
  initialising: "CAMERA_INITIALISING",
  denied: "CAMERA_PERMISSION_DENIED",
  blocked_by_overlay: "CAMERA_PERMISSION_BLOCKED_BY_OVERLAY",
  not_found: "CAMERA_NOT_FOUND",
  in_use: "CAMERA_IN_USE",
  security: "CAMERA_UNAVAILABLE",
  unavailable: "CAMERA_UNAVAILABLE",
  unsupported: "CAMERA_UNSUPPORTED",
  error: "CAMERA_ERROR",
};

export default function ScannerScreen() {
  const navigate = useNavigate();
  const { videoRef, status, start, stop, captureFrame } = useCamera();
  const fileRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<number | null>(null);
  const [phase, setPhase] = useState<ScanPhase>("ready");
  const [preview, setPreview] = useState<string | null>(null);
  const [docName, setDocName] = useState<string | null>(null);
  const [dropActive, setDropActive] = useState(false);

  const isActive = status === "active";
  const isError = CAMERA_ERROR_STATES.includes(status);
  const isBusy = status === "initialising";
  const hasMedia = !!preview || !!docName;
  const processing = phase === "processing";

  // Clear any pending navigation timer on unmount (prevents forced redirect after leaving).
  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current); }, []);

  // Show CAMERA_READY briefly when the stream goes live, then start scanning.
  useEffect(() => {
    if (status === "active" && phase === "ready" && !hasMedia) {
      const t = window.setTimeout(() => setPhase("scanning"), 600);
      return () => window.clearTimeout(t);
    }
  }, [status, phase, hasMedia]);

  const submit = useCallback((src: string | null, doc: string | null) => {
    setPreview(src);
    setDocName(doc);
    setPhase("processing");
    stop();
    timerRef.current = window.setTimeout(() => navigate("/dashboard"), PROCESS_MS);
  }, [navigate, stop]);

  // Camera is requested ONLY from this explicit user action.
  const activateCamera = useCallback(() => { void start(); }, [start]);

  const onCapture = () => {
    const frame = captureFrame();
    if (frame) submit(frame, null);
  };

  const onFile = (file: File) => {
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => submit(String(reader.result), null);
      reader.readAsDataURL(file);
    } else {
      submit(null, file.name || "Document");
    }
  };

  const pickFile = () => fileRef.current?.click();

  const statusLabel = processing
    ? "PROCESSING"
    : isActive
      ? PHASE_STATUS[phase]
      : STATUS_LABEL[status] ?? "CAMERA_PERMISSION_REQUIRED";
  const showError = isError && !hasMedia && !processing;

  return (
    <AppShell>
      <section data-testid="scanner-screen" className="mx-auto flex min-h-screen w-full max-w-3xl flex-col gap-6 px-5 py-6 md:px-10 md:py-10">
        <input
          ref={fileRef}
          data-testid="scanner-file-input"
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
        />

        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col gap-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cad">Plan scanner</span>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">Capture your plan</h1>
        </motion.div>

        <div className="relative">
          <div className={cn(showError && "pointer-events-none opacity-40")}>
            <motion.div initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
              <Viewfinder
                videoRef={videoRef}
                cameraActive={isActive}
                phase={phase}
                preview={preview}
                docName={docName}
                dropActive={dropActive}
                onDrop={onFile}
                onDragState={setDropActive}
                onPickFile={pickFile}
              />
            </motion.div>
          </div>
          {showError && <CameraErrorState status={status} onRetry={activateCamera} onUpload={pickFile} />}
        </div>

        <div className="flex flex-col items-center gap-6">
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={statusLabel}
              data-testid="scanner-status"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className={cn("px-4 text-center font-mono text-xs uppercase tracking-[0.22em]", (isActive && phase === "ready") || status === "idle" ? "text-cad" : "text-safety")}
            >
              [STATUS: {statusLabel}]
            </motion.p>
          </AnimatePresence>

          {/* Active camera: capture + upload */}
          {isActive && !processing && (
            <div className="flex w-full items-center justify-center gap-6 sm:gap-10">
              <span className="hidden w-36 sm:block" aria-hidden />
              <motion.button
                type="button"
                data-testid="scanner-capture-button"
                aria-label="Capture"
                onClick={onCapture}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.94 }}
                className="relative flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-safety shadow-[0_0_48px_-8px_rgba(255,91,0,0.8)]"
              >
                <span aria-hidden className="absolute inset-[-8px] rounded-full border border-safety/40" />
                <span aria-hidden className={cn("absolute inset-[-8px] rounded-full border-2 border-safety/70", phase === "scanning" && "animate-ping opacity-40")} />
                <span className="h-14 w-14 rounded-full border-[3px] border-obsidian/80" />
              </motion.button>
              <motion.button
                type="button"
                data-testid="scanner-upload-button"
                onClick={pickFile}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex h-12 w-36 items-center justify-center rounded-2xl border border-cad/60 font-display text-sm font-semibold text-white/85 transition-colors duration-200 hover:border-white/60 hover:text-white"
              >
                Upload File
              </motion.button>
            </div>
          )}

          {/* Idle / initialising: explicit Activate Camera + independent Upload */}
          {!isActive && !showError && !processing && (
            <div className="flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <motion.button
                type="button"
                data-testid="scanner-activate-camera"
                onClick={activateCamera}
                disabled={isBusy}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-safety font-display text-sm font-semibold text-obsidian shadow-[0_0_28px_-10px_rgba(255,91,0,0.8)] transition-opacity duration-200 hover:opacity-90 disabled:opacity-60 sm:w-48"
              >
                <Camera size={16} />
                {isBusy ? "Starting…" : "Activate Camera"}
              </motion.button>
              <motion.button
                type="button"
                data-testid="scanner-upload-button"
                onClick={pickFile}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex h-12 w-full items-center justify-center rounded-2xl border border-cad/60 font-display text-sm font-semibold text-white/85 transition-colors duration-200 hover:border-white/60 hover:text-white sm:w-48"
              >
                Upload File
              </motion.button>
            </div>
          )}
        </div>
      </section>
    </AppShell>
  );
}
